// Cloudflare Turnstile. Only anonymous requests to the few open endpoints need
// a token — see the interceptor in api.js.
//
// Normally invisible: Turnstile decides from browser-integrity checks and IP
// reputation, and issues a token with no UI. When those signals are ambiguous
// it falls back to an interactive checkbox, so the widget lives in an overlay
// we reveal for exactly that case — a hidden container would leave the user
// with an unclickable challenge and a request that never completes.
const SITE_KEY = process.env.VUE_APP_TURNSTILE_SITE_KEY;
const SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const TIMEOUT_MS = 30000;
// Turnstile tokens live 300s; refresh well inside that.
const TOKEN_TTL_MS = 240000;

let widgetId = null;
let loading = null;
let pending = null;
let overlay = null;
let cached = null;

function settle(fn, value) {
  if (overlay) overlay.style.display = "none";
  if (!pending) return;
  const current = pending;
  pending = null;
  clearTimeout(current.timer);
  current[fn](value);
}

// A token nobody is waiting for yet: keep it so the next submit is instant.
function onToken(token) {
  if (pending) settle("resolve", token);
  else cached = { token, at: Date.now() };
}

// `fresh` skips the reset for a widget that has never run. Guarded because a
// throw from the Turnstile API must not escape into the load promise.
function execute(fresh) {
  try {
    if (!fresh) window.turnstile.reset(widgetId);
    window.turnstile.execute(widgetId);
  } catch (e) {
    console.warn("captcha: execute falló —", e.message);
    settle("reject", new Error("captcha: " + e.message));
  }
}

function load() {
  if (loading) return loading;
  loading = new Promise((resolve, reject) => {
    const started = Date.now();
    const script = document.createElement("script");
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    // NOTE: do not wrap this in turnstile.ready() — that helper is only for
    // synchronously loaded scripts and throws outright when the tag carries
    // async/defer. With async/defer the API is already initialised by onload,
    // so render() can be called directly here.
    script.onload = () => {
      overlay = document.createElement("div");
      overlay.style.cssText =
        "position:fixed;inset:0;z-index:2147483647;display:none;" +
        "align-items:center;justify-content:center;background:rgba(0,0,0,.5)";
      const slot = document.createElement("div");
      overlay.appendChild(slot);
      document.body.appendChild(overlay);

      widgetId = window.turnstile.render(slot, {
        sitekey: SITE_KEY,
        execution: "execute",
        appearance: "interaction-only",
        callback: onToken,
        "error-callback": () =>
          settle("reject", new Error("captcha: verificación fallida")),
        "timeout-callback": () => settle("reject", new Error("captcha: expiró")),
        "before-interactive-callback": () => {
          // Only when someone is actually waiting for a token. The warm-up
          // below prefetches one at page load, and if Turnstile wants an
          // interaction for THAT, showing the overlay walls off a page whose
          // visitor hasn't tried to submit anything — on slow mobile data the
          // demo chatbots landed on "Verify you are human" instead of the
          // chat and its Google button. Left hidden, that challenge just
          // expires; the next gated request resets the widget (see execute)
          // and gets a fresh challenge, overlay included, when it matters.
          if (pending) overlay.style.display = "flex";
        },
        "after-interactive-callback": () => {
          overlay.style.display = "none";
        }
      });
      console.debug("captcha: widget listo en", Date.now() - started, "ms");
      // Resolve BEFORE prefetching: a throw from execute() must not leave the
      // load promise unsettled, which would hang every caller awaiting it.
      resolve();
      // Solve one now so the first submit doesn't wait for a challenge.
      execute(true);
    };
    script.onerror = () => reject(new Error("captcha: script bloqueado"));
    document.head.appendChild(script);
  });
  return loading;
}

// Downloading Cloudflare's script and rendering the widget is the slow part;
// executing an already-rendered widget is fast. Entries that host a gated form
// call this at startup so a submit doesn't pay for it. Opt-in per entry: pages
// with no gated call shouldn't pull a third-party script at all.
export function warmCaptcha() {
  if (!SITE_KEY || typeof window === "undefined") return;
  const warm = () => load().catch(() => {});
  // The timeout matters: on a heavy app boot idle time may not arrive for
  // many seconds, and without it the warm-up loses its whole point.
  if (window.requestIdleCallback) window.requestIdleCallback(warm, { timeout: 1500 });
  else setTimeout(warm, 500);
}

// Tokens are single-use, so each call consumes one and queues the next.
export async function getCaptchaToken() {
  if (!SITE_KEY) {
    // Silent "" here reaches the backend as "sin token", which is
    // indistinguishable from a failed challenge. Say so out loud.
    console.warn(
      "captcha: falta VUE_APP_TURNSTILE_SITE_KEY — reinicia el dev server " +
        "tras editar .env.development, o reconstruye el bundle."
    );
    return "";
  }
  await load();

  if (cached && Date.now() - cached.at < TOKEN_TTL_MS) {
    const { token } = cached;
    cached = null;
    execute(); // refill in the background for the next call
    return token;
  }
  cached = null;

  const started = Date.now();
  return new Promise((resolve, reject) => {
    // Never leave a form spinning if Turnstile goes quiet.
    const timer = setTimeout(
      () => settle("reject", new Error("captcha: sin respuesta")),
      TIMEOUT_MS
    );
    pending = {
      resolve: (token) => {
        console.debug("captcha: token en", Date.now() - started, "ms");
        resolve(token);
      },
      reject,
      timer
    };
    execute();
  });
}
